import { defineStore } from 'pinia';

const STORAGE_KEY = 'ap.stories.seen';
const STORAGE_KEY_LAST_SLIDE = 'ap.stories.lastSlide';
const STORAGE_KEY_STORIES_HASH = 'ap.stories.hash';
const STORAGE_KEY_SEEN_URLS = 'ap.stories.seenUrls';

export const useStoriesStore = defineStore('ap.stories', () => {
  const stories = ref<Story[]>([]);
  const seenStoriesHash = ref<string | null>(null);
  const lastSlideIndex = ref<number>(0);
  const seenUrls = ref<string[]>([]);

  // Gera um hash único baseado nos stories atuais
  const getCurrentStoriesHash = (): string => {
    if (stories.value.length === 0) return '';

    // Usa os imgUrls como identificador único dos stories
    return stories.value.map((s) => s.imgUrl).join('|');
  };

  // Carrega o hash salvo do localStorage
  const loadSeenHash = () => {
    if (import.meta.client) {
      seenStoriesHash.value = localStorage.getItem(STORAGE_KEY);
    }
  };

  // Carrega as URLs dos stories já vistos
  const loadSeenUrls = () => {
    if (import.meta.client) {
      const saved = localStorage.getItem(STORAGE_KEY_SEEN_URLS);

      seenUrls.value = saved ? JSON.parse(saved) : [];
    }
  };

  // Encontra o índice do primeiro story não visto
  const findFirstUnseenIndex = (): number => {
    if (seenUrls.value.length === 0) return 0;

    const index = stories.value.findIndex(
      (story) => !seenUrls.value.includes(story.imgUrl),
    );

    return index === -1 ? 0 : index;
  };

  // Carrega o último slide visto (apenas se os stories não mudaram)
  const loadLastSlideIndex = () => {
    if (import.meta.client) {
      const savedHash = localStorage.getItem(STORAGE_KEY_STORIES_HASH);
      const currentHash = getCurrentStoriesHash();

      // Só retoma do último slide se os stories não mudaram
      if (savedHash === currentHash) {
        const savedIndex = localStorage.getItem(STORAGE_KEY_LAST_SLIDE);

        lastSlideIndex.value = savedIndex ? parseInt(savedIndex, 10) : 0;
      } else {
        // Stories mudaram, encontra o primeiro não visto
        lastSlideIndex.value = findFirstUnseenIndex();
      }
    }
  };

  // Salva o último slide visto
  const saveLastSlideIndex = (index: number) => {
    if (import.meta.client) {
      lastSlideIndex.value = index;
      localStorage.setItem(STORAGE_KEY_LAST_SLIDE, index.toString());
      localStorage.setItem(STORAGE_KEY_STORIES_HASH, getCurrentStoriesHash());

      // Salva as URLs dos stories vistos até o índice atual
      const currentSeenUrls = stories.value
        .slice(0, index + 1)
        .map((s) => s.imgUrl);

      const allSeenUrls = [...new Set([...seenUrls.value, ...currentSeenUrls])];

      seenUrls.value = allSeenUrls;
      localStorage.setItem(STORAGE_KEY_SEEN_URLS, JSON.stringify(allSeenUrls));
    }
  };

  // Marca os stories atuais como vistos
  const markStoriesAsSeen = () => {
    if (import.meta.client) {
      const hash = getCurrentStoriesHash();

      localStorage.setItem(STORAGE_KEY, hash);
      seenStoriesHash.value = hash;

      // Salva todas as URLs como vistas
      const allUrls = stories.value.map((s) => s.imgUrl);
      const allSeenUrls = [...new Set([...seenUrls.value, ...allUrls])];

      seenUrls.value = allSeenUrls;
      localStorage.setItem(STORAGE_KEY_SEEN_URLS, JSON.stringify(allSeenUrls));

      // Limpa o último slide salvo pois já viu tudo
      localStorage.removeItem(STORAGE_KEY_LAST_SLIDE);
      lastSlideIndex.value = 0;
    }
  };

  function setStories(newStories: Story[]) {
    stories.value = newStories;
    loadSeenHash();
    loadSeenUrls();
    loadLastSlideIndex();
  }

  const hasStories = computed(() => stories.value.length > 0);

  // Verifica se há stories não vistos
  const hasUnseenStories = computed(() => {
    if (!hasStories.value) return false;

    return getCurrentStoriesHash() !== seenStoriesHash.value;
  });

  return {
    stories,
    setStories,
    hasStories,
    hasUnseenStories,
    markStoriesAsSeen,
    lastSlideIndex,
    saveLastSlideIndex,
  };
});

export type Story = {
  id: string;
  title: string;
  description: string | null;
  linkUrl: string | null;
  buttonText: string | null;
  buttonColor: string | null;
  buttonTextColor: string | null;
  imgUrl: string;
  media_type: 'image' | 'video';
};
